import * as React from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cn } from '../lib/cn.js';

// Encart d'information ou d'alerte (voir .claude/skills/interface-sobre) : surface et cadre
// neutres, la tonalité ne colore que l'icône.

export type NoticeTone = 'info' | 'success' | 'warning' | 'danger';

const ICON: Record<NoticeTone, { Icon: typeof Info; className: string }> = {
  info: { Icon: Info, className: 'text-primary' },
  success: { Icon: CheckCircle2, className: 'text-success' },
  warning: { Icon: AlertTriangle, className: 'text-warning' },
  danger: { Icon: XCircle, className: 'text-destructive' },
};

export interface NoticeProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: NoticeTone;
  title?: React.ReactNode;
  /** Remplace l'icône par défaut de la tonalité (null : pas d'icône) */
  icon?: React.ReactNode | null;
  action?: React.ReactNode;
}

export function Notice({ tone = 'info', title, icon, action, className, children, ...props }: NoticeProps) {
  const { Icon, className: iconClass } = ICON[tone];
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm', className)}
      {...props}
    >
      {icon !== null && (
        <span className={cn('mt-0.5 shrink-0 [&>svg]:size-4', iconClass)}>{icon ?? <Icon className="size-4" />}</span>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        {title && <p className="font-medium text-foreground">{title}</p>}
        {children && <div className="text-muted-foreground">{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}
